import { useState, useEffect } from "react";

type DarkModeState = {
  isDarkMode: boolean;
  toggle: () => void;
  enable: () => void;
  disable: () => void;
};

export function useDarkMode(): DarkModeState {
  // Initialize with system preference if no stored preference exists
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      const storedValue = window.localStorage.getItem("darkMode");
      if (storedValue !== null) {
        return storedValue === "true";
      }
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  // Update localStorage and apply dark mode class
  useEffect(() => {
    // Save to localStorage
    localStorage.setItem("darkMode", isDarkMode.toString());
    
    // Apply the class to document
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  // Add system preference listener
  useEffect(() => {
    // Only proceed if no user preference is stored
    if (!localStorage.getItem("darkMode")) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      
      const handleChange = (e: MediaQueryListEvent) => {
        setIsDarkMode(e.matches);
      };
      
      // Add listener for system preference changes
      mediaQuery.addEventListener("change", handleChange);
      
      // Clean up
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, []);

  const toggle = () => setIsDarkMode((prev) => !prev);
  const enable = () => setIsDarkMode(true);
  const disable = () => setIsDarkMode(false);

  return {
    isDarkMode,
    toggle,
    enable,
    disable
  };
}
